import {
    PublicCourse,
    PublicEducator,
    PublicOccurrence,
} from '@cademy-package/sdk-component-types';
import { FunctionComponent, useMemo } from 'react';
import { getBookingUrl } from '../../../../services/getBookingUrl';
import styles from './styles.module.scss';
import { useEmbedExternalLinkProps } from '../../../../hooks/useEmbedExternalLinkProps';
import Link from 'next/link';
import { useMinisiteLink } from '../../../../hooks/useMinisiteLink';
import Popover from '../../../Popover';
import Button from '../../../Button';
import AngleDownRegularIcon from '../../../Icons/AngleDownRegularIcon';
import ButtonLink from '../../../ButtonLink';

const DateRequestLink: FunctionComponent<{
    course: PublicCourse;
    educator: PublicEducator;
    isEmbed: boolean;
    occurrences: Array<PublicOccurrence>;
}> = ({ course, educator, isEmbed, occurrences }) => {
    const { target, rel } = useEmbedExternalLinkProps(isEmbed);
    const dateEnquiry = (course?.enquiry_types || []).includes('date');
    const isOnRequest = course?.type === 'on-request';
    if ((!dateEnquiry && !isOnRequest) || course.type === 'on-demand') return null;
    if (occurrences.length === 0) {
        return null;
    }

    return (
        <Link
            href={getBookingUrl({
                courseSlug: course.slug,
                courseType: course.type,
                educatorSlug: educator.slug,
                isEmbed,
                isRequest: true,
            })}
            rel={rel}
            target={target}
        >
            Request a Date
        </Link>
    );
};

const GroupEnquiryLink: FunctionComponent<{
    course: PublicCourse;
    educator: PublicEducator;
    isEmbed: boolean;
}> = ({ course, educator, isEmbed }) => {
    const { target, rel } = useEmbedExternalLinkProps(isEmbed);
    const groupEnquiry = (course?.enquiry_types || []).includes('group');
    if (course.type === 'on-demand' || !groupEnquiry) return null;

    return (
        <Link
            href={getBookingUrl({
                courseSlug: course.slug,
                courseType: course.type,
                educatorSlug: educator.slug,
                isEmbed,
                isRequest: true,
                isGroup: true,
            })}
            rel={rel}
            target={target}
        >
            Group Booking
        </Link>
    );
};

const ContactEducatorLink: FunctionComponent<{
    course: PublicCourse;
    educator: PublicEducator;
    isEmbed: boolean;
    isButton?: boolean;
    isMarketplace?: boolean;
}> = ({ course, educator, isEmbed, isButton = false, isMarketplace = false }) => {
    const { target, rel } = useEmbedExternalLinkProps(isEmbed);
    const minisiteUrl = useMinisiteLink(educator.slug, '/enquiry');
    const contactUsUrl = useMemo(() => {
        if (isEmbed) {
            return `${minisiteUrl}?type=message&educator_slug=${educator.slug}&educator_name=${encodeURIComponent(educator.name)}&course_ref=${course.slug}&course_name=${encodeURIComponent(course.name)}`;
        }
        if (isMarketplace) {
            return `/${educator.slug}/enquiry?type=message&educator_slug=${educator.slug}&educator_name=${encodeURIComponent(educator.name)}&course_ref=${course.slug}&course_name=${encodeURIComponent(course.name)}`;
        }
        return `/enquiry?type=message&educator_slug=${educator.slug}&educator_name=${encodeURIComponent(educator.name)}&course_ref=${course.slug}&course_name=${encodeURIComponent(course.name)}`;
    }, [course, educator, isEmbed, minisiteUrl, isMarketplace]);

    if (isButton === true) {
        return (
            <ButtonLink
                href={contactUsUrl}
                rel={rel}
                target={target}
                className={styles.contactAndMoreBtn}
            >
                Contact Us
            </ButtonLink>
        );
    }

    return (
        <Link href={contactUsUrl} rel={rel} target={target}>
            Contact Us
        </Link>
    );
};

const VouchersLink: FunctionComponent<{
    course: PublicCourse;
    educatorSlug: string;
    isMarketplace: boolean;
    isVoucherAppInstalled: boolean;
    isEmbed: boolean;
}> = ({ course, educatorSlug, isMarketplace, isVoucherAppInstalled, isEmbed }) => {
    const { target, rel } = useEmbedExternalLinkProps(isEmbed);
    const minisiteUrl = useMinisiteLink(educatorSlug, '/gift-checkout');
    const giftCheckoutUrl = useMemo(() => {
        if (isEmbed) {
            return `${minisiteUrl}?course_ref=${course.slug}&educator_ref=${course.educator_ref}`;
        }
        return `/gift-checkout?course_ref=${course.slug}&educator_ref=${course.educator_ref}`;
    }, [course, isEmbed, minisiteUrl]);

    if (!isVoucherAppInstalled || isMarketplace) return null;

    return (
        <Link href={giftCheckoutUrl} rel={rel} target={target}>
            Gift Vouchers
        </Link>
    );
};

export type SecondaryButtonsProps = {
    course: PublicCourse;
    educator: PublicEducator;
    isEmbed: boolean;
    isMarketplace: boolean;
    isVoucherAppInstalled: boolean;
    occurrences: Array<PublicOccurrence>;
};

export const SecondaryButtons: FunctionComponent<SecondaryButtonsProps> = ({
    course,
    educator,
    isEmbed,
    isMarketplace,
    isVoucherAppInstalled,
    occurrences,
}) => {
    const hasMultipleSecondaryOptions = useMemo(() => {
        if (!isMarketplace && isVoucherAppInstalled) return true;
        if (['class', 'blended'].includes(course.type) && course.enquiry_types.length) return true;
        return false;
    }, [course, isMarketplace, isVoucherAppInstalled]);

    if (hasMultipleSecondaryOptions) {
        return (
            <div>
                <Popover
                    className={styles.popover}
                    content={
                        <div className={styles.popoverContents}>
                            <ContactEducatorLink
                                course={course}
                                educator={educator}
                                isEmbed={isEmbed}
                                isMarketplace={isMarketplace}
                            />
                            <DateRequestLink
                                course={course}
                                educator={educator}
                                isEmbed={isEmbed}
                                occurrences={occurrences}
                            />
                            <GroupEnquiryLink
                                course={course}
                                educator={educator}
                                isEmbed={isEmbed}
                            />
                            <VouchersLink
                                course={course}
                                educatorSlug={educator.slug}
                                isVoucherAppInstalled={isVoucherAppInstalled}
                                isMarketplace={isMarketplace}
                                isEmbed={isEmbed}
                            />
                        </div>
                    }
                >
                    <Button className={styles.contactAndMoreBtn}>
                        Contact & More
                        <AngleDownRegularIcon width={12} />
                    </Button>
                </Popover>
            </div>
        );
    }

    return (
        <ContactEducatorLink
            course={course}
            educator={educator}
            isEmbed={isEmbed}
            isButton={true}
        />
    );
};
